import React, { useState } from 'react';
import { supabase } from '../../lib/supabaseClient';
import { SKILL_LABELS, calculateFormMatch } from '../../lib/testCalculations';

const C = { turquesa:'#5dc1b9', mango:'#FDBE02', coral:'#eb636b', morado:'#534AB7', verde:'#1D9E75' };
const font = "'DM Sans', sans-serif";
const glass = { background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:16 };
const ROUTE_COLORS = { tiempo:C.turquesa, productos:C.mango, activos:C.morado };
const ROUTE_EMOJI = { tiempo:'⏰', productos:'📦', activos:'🏗️' };

export default function FormaCard({ forma, userRadar, userId, existingChoice, onChoiceUpdate }) {
  const [saving, setSaving] = useState(false);
  const [choice, setChoice] = useState(existingChoice || null);

  const matchPct = calculateFormMatch(forma, userRadar);
  const routeColor = ROUTE_COLORS[forma.route] || C.turquesa;
  const skills = forma.required_skills || [];
  const pros = forma.pros || [];
  const cons = forma.cons || [];

  const handleChoice = async (status) => {
    if (!userId || saving) return;
    setSaving(true);
    const { error } = await supabase.from('user_form_choices').upsert({
      user_id: userId, form_id: forma.id, status, chosen_at: new Date().toISOString(),
    }, { onConflict: 'user_id,form_id' });
    if (!error) {
      setChoice(status);
      if (onChoiceUpdate) onChoiceUpdate(forma.id, status);
    }
    setSaving(false);
  };

  return (
    <div style={{ ...glass, padding:'24px 20px', marginBottom:16 }}>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:12, flexWrap:'wrap' }}>
        <span style={{ fontSize:10, fontWeight:600, padding:'3px 10px', borderRadius:50, background:`${routeColor}15`, color:routeColor, fontFamily:font }}>
          {ROUTE_EMOJI[forma.route]} {forma.route?.toUpperCase()}
        </span>
        <span style={{ fontSize:10, padding:'3px 10px', borderRadius:50, background:'rgba(255,255,255,0.06)', color:'rgba(255,255,255,0.5)', fontFamily:font }}>
          Match {matchPct}%
        </span>
      </div>

      <h3 style={{ fontSize:18, fontWeight:500, margin:'0 0 6px', color:'#fff', fontFamily:font }}>{forma.name}</h3>
      <p style={{ fontSize:13, color:'rgba(255,255,255,0.6)', lineHeight:1.6, margin:'0 0 16px', fontFamily:font }}>{forma.short_description}</p>

      {/* Metrics */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(2, 1fr)', gap:8, marginBottom:16 }}>
        {[
          { label:'Ingresos', value:`$${forma.income_min}-$${forma.income_max} USD/mes` },
          { label:'Primer $', value:forma.time_to_first_income },
          { label:'Inversión', value:forma.investment_max === 0 ? '$0' : `$${forma.investment_min}-$${forma.investment_max} USD` },
          { label:'Match', value:`${matchPct}%`, color: matchPct >= 70 ? C.verde : matchPct >= 50 ? C.mango : C.coral },
        ].map((m, i) => (
          <div key={i} style={{ padding:'10px 12px', borderRadius:10, background:'rgba(255,255,255,0.03)', border:'0.5px solid rgba(255,255,255,0.06)' }}>
            <p style={{ fontSize:10, color:'rgba(255,255,255,0.4)', margin:'0 0 2px', fontFamily:font }}>{m.label}</p>
            <p style={{ fontSize:13, fontWeight:500, margin:0, color:m.color || '#fff', fontFamily:font }}>{m.value}</p>
          </div>
        ))}
      </div>

      {/* Case real */}
      {forma.case_real_name && (
        <div style={{ padding:'14px 16px', borderRadius:12, background:'rgba(255,255,255,0.03)', border:'0.5px solid rgba(255,255,255,0.08)', marginBottom:16 }}>
          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8 }}>
            <div style={{ width:36, height:36, borderRadius:'50%', background:routeColor, display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, fontWeight:600, color:'#fff', fontFamily:font }}>
              {forma.case_real_initials}
            </div>
            <div>
              <p style={{ fontSize:13, fontWeight:500, margin:0, fontFamily:font }}>{forma.case_real_name}</p>
              <p style={{ fontSize:11, color:'rgba(255,255,255,0.4)', margin:0, fontFamily:font }}>📍 {forma.case_real_location}</p>
            </div>
          </div>
          <p style={{ fontSize:12, color:'rgba(255,255,255,0.6)', lineHeight:1.6, margin:0, fontFamily:font, fontStyle:'italic' }}>
            "{forma.case_real_description}"
          </p>
        </div>
      )}

      {/* Skills match */}
      <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:16 }}>
        {skills.map(skill => {
          const level = userRadar?.[skill] || 0;
          const has = level >= 6;
          return (
            <span key={skill} style={{
              fontSize:11, padding:'4px 10px', borderRadius:50, fontFamily:font,
              background: has ? 'rgba(29,158,117,0.1)' : 'rgba(253,190,2,0.1)',
              color: has ? C.verde : C.mango,
              border: `0.5px solid ${has ? C.verde : C.mango}30`,
            }}>
              {has ? '✓' : '!'} {SKILL_LABELS[skill] || skill}
            </span>
          );
        })}
      </div>

      {/* Pros / Cons */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }}>
        <div>
          <p style={{ fontSize:11, color:C.verde, fontWeight:500, margin:'0 0 6px', fontFamily:font }}>A FAVOR</p>
          {pros.map((p, i) => (
            <p key={i} style={{ fontSize:12, color:'rgba(255,255,255,0.6)', margin:'0 0 4px', lineHeight:1.5, fontFamily:font }}>✓ {p}</p>
          ))}
        </div>
        <div>
          <p style={{ fontSize:11, color:C.coral, fontWeight:500, margin:'0 0 6px', fontFamily:font }}>EN CONTRA</p>
          {cons.map((c, i) => (
            <p key={i} style={{ fontSize:12, color:'rgba(255,255,255,0.6)', margin:'0 0 4px', lineHeight:1.5, fontFamily:font }}>✗ {c}</p>
          ))}
        </div>
      </div>

      {/* Choice buttons */}
      <div style={{ display:'flex', gap:8 }}>
        {[
          { status:'si', label:'✓ Sí me resuena', color:C.verde },
          { status:'tal_vez', label:'? Tal vez', color:C.mango },
          { status:'no', label:'✗ No es para mí', color:C.coral },
        ].map(btn => (
          <button key={btn.status} onClick={() => handleChoice(btn.status)} disabled={saving} style={{
            flex:1, padding:'10px 8px', fontSize:12, fontWeight:500, fontFamily:font, cursor:'pointer', borderRadius:10,
            background: choice === btn.status ? `${btn.color}20` : 'transparent',
            border: `1px solid ${choice === btn.status ? btn.color : 'rgba(255,255,255,0.1)'}`,
            color: choice === btn.status ? btn.color : 'rgba(255,255,255,0.5)',
            transition: 'all 0.2s',
          }}>
            {btn.label}
          </button>
        ))}
      </div>
    </div>
  );
}
